import { useEffect, useSyncExternalStore } from "react";

/**
 * Light and dark follow the system, with a manual override kept in localStorage. The
 * resolved theme is applied as the `dark` class (and `color-scheme`) on <html>; index.html
 * runs the same logic inline before the first paint so there is no flash.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "minket.theme";
const QUERY = "(prefers-color-scheme: dark)";

const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

let preference: ThemePreference = readPreference();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const media = window.matchMedia(QUERY);
  media.addEventListener("change", listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) {
      preference = readPreference();
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    media.removeEventListener("change", listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function isThemePreference(value: string): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (pref === "system") return systemDark ? "dark" : "light";
  return pref;
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    if (next === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Private mode or storage disabled: the choice lasts for the session.
  }
  emit();
}

/** Applies a theme as the `dark` class and `color-scheme` on <html>. */
export function applyTheme(theme: ResolvedTheme, root: HTMLElement = document.documentElement) {
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

const systemDarkNow = () => window.matchMedia(QUERY).matches;

/** The stored preference and the theme that results from it, live. */
export function useTheme() {
  const pref = useSyncExternalStore(
    subscribe,
    () => preference,
    () => "system" as const,
  );
  const systemDark = useSyncExternalStore(subscribe, systemDarkNow, () => false);
  return {
    preference: pref,
    resolved: resolveTheme(pref, systemDark),
    setPreference: setThemePreference,
  };
}

/** Call once at the root: keeps <html class="dark"> in sync with the resolved theme. */
export function useApplyTheme(): ResolvedTheme {
  const { resolved } = useTheme();
  useEffect(() => applyTheme(resolved), [resolved]);
  return resolved;
}
